import React from 'react';
import { Alert, Box, Button, Card, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { LongestStalled, ReadyListItem } from '../onboardingIntelligence.types';
import { updatePriority } from '../services/OnboardingIntelligenceService';
import { useMountedRef } from '../hooks/useMountedRef';
import { useBulkPush } from '../hooks/useBulkPush';
import {
    CARD_SX,
    HEADING_NAVY,
    PILL_BUTTON_SX,
    SECTION_LABEL_SX,
} from './onboardingIntelligence.styles';

interface RecommendationCardProps {
    number: string;
    title: React.ReactNode;
    cta: React.ReactNode;
    testId: string;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
    number,
    title,
    cta,
    testId,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 0',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            '&:last-of-type': { borderBottom: 'none' },
        }}
        data-testid={testId}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography
                variant='body2'
                sx={{
                    color: HEADING_NAVY,
                    fontWeight: 700,
                    fontFamily: 'Poppins',
                    minWidth: '24px',
                }}
            >
                {number}
            </Typography>
            <Typography variant='body2' sx={{ color: HEADING_NAVY }}>
                {title}
            </Typography>
        </Box>
        {cta}
    </Box>
);

interface Props {
    notificationsOn: boolean;
    longestStalled?: LongestStalled;
    readyList: ReadyListItem[];
    onRefresh: () => void;
}

const RecommendationsSection: React.FC<Props> = ({
    notificationsOn,
    longestStalled,
    readyList,
    onRefresh,
}) => {
    const [priorityError, setPriorityError] = React.useState<string | null>(null);
    const [priorityBusy, setPriorityBusy] = React.useState(false);
    const mountedRef = useMountedRef();

    const bulk = useBulkPush(onRefresh);

    const showNotifs = !notificationsOn;
    const showStalled = Boolean(longestStalled && longestStalled.priority !== 'High');
    const showReady = readyList.length > 0;
    const allHidden = !showNotifs && !showStalled && !showReady;

    const handleUpdatePriority = async () => {
        if (!longestStalled) return;
        setPriorityBusy(true);
        setPriorityError(null);
        try {
            await updatePriority(longestStalled.id, 'High');
            if (mountedRef.current) onRefresh();
        } catch (err) {
            if (mountedRef.current) {
                setPriorityError(
                    err instanceof Error ? err.message : 'Could not update priority'
                );
            }
        } finally {
            if (mountedRef.current) setPriorityBusy(false);
        }
    };

    const handleBulkPush = () =>
        bulk.run(readyList.map((r) => ({ id: r.id, companyName: r.companyName })));

    const alerts = (
        <>
            {priorityError && (
                <Alert
                    severity='error'
                    role='alert'
                    sx={{ marginBottom: '8px' }}
                >
                    {priorityError}
                </Alert>
            )}
            {bulk.error && (
                <Alert
                    severity='error'
                    role='alert'
                    sx={{ marginBottom: '8px' }}
                >
                    {bulk.error}
                </Alert>
            )}
            {bulk.failures.length > 0 && (
                <Alert
                    severity='warning'
                    role='alert'
                    sx={{ marginBottom: '8px' }}
                    data-testid='onboarding-intelligence-recommendations-bulk-failures'
                >
                    {bulk.failures.length} could not be pushed:{' '}
                    {bulk.failures.map((f) => f.companyName).join(', ')}
                </Alert>
            )}
        </>
    );

    const cardSx = { ...CARD_SX, padding: '16px 20px' } as const;
    const headerSx = { ...SECTION_LABEL_SX, marginBottom: '8px' };

    if (allHidden) {
        return (
            <Card data-testid='onboarding-intelligence-recommendations' sx={cardSx}>
                <Typography variant='subtitle2' sx={headerSx}>
                    Recommendations
                </Typography>
                {alerts}
                <Typography
                    variant='body2'
                    sx={{ color: 'rgba(0,0,0,0.6)' }}
                    data-testid='onboarding-intelligence-recommendations-empty'
                >
                    You&rsquo;re all caught up — no recommendations right now.
                </Typography>
            </Card>
        );
    }

    return (
        <Card data-testid='onboarding-intelligence-recommendations' sx={cardSx}>
            <Typography variant='subtitle2' sx={headerSx}>
                Recommendations
            </Typography>
            {alerts}
            {showNotifs && (
                <RecommendationCard
                    testId='onboarding-intelligence-recommendation-01'
                    number='01'
                    title={
                        <>
                            <strong>Turn on automated notifications</strong> for suppliers
                            idle 14+ days
                        </>
                    }
                    cta={
                        <Button
                            variant='outlined'
                            size='small'
                            component={Link}
                            to='/onboarding/settings#reminders'
                            sx={PILL_BUTTON_SX}
                            data-testid='onboarding-intelligence-recommendation-01-cta'
                        >
                            Go To Settings
                        </Button>
                    }
                />
            )}
            {showStalled && longestStalled && (
                <RecommendationCard
                    testId='onboarding-intelligence-recommendation-02'
                    number='02'
                    title={
                        <>
                            <strong>Reassign {longestStalled.companyName}</strong> from{' '}
                            {longestStalled.priority} to High priority — at{' '}
                            {longestStalled.days} days, this is your longest-stalled supplier
                        </>
                    }
                    cta={
                        <Button
                            variant='outlined'
                            size='small'
                            disabled={priorityBusy}
                            aria-busy={priorityBusy || undefined}
                            onClick={handleUpdatePriority}
                            sx={PILL_BUTTON_SX}
                            data-testid='onboarding-intelligence-recommendation-02-cta'
                        >
                            Update Priority
                        </Button>
                    }
                />
            )}
            {showReady && (
                <RecommendationCard
                    testId='onboarding-intelligence-recommendation-03'
                    number='03'
                    title={
                        <>
                            <strong>Bulk update the {readyList.length} ready suppliers</strong>
                        </>
                    }
                    cta={
                        <Button
                            variant='outlined'
                            size='small'
                            disabled={bulk.busy}
                            aria-busy={bulk.busy || undefined}
                            onClick={handleBulkPush}
                            sx={PILL_BUTTON_SX}
                            data-testid='onboarding-intelligence-recommendation-03-cta'
                        >
                            Push Suppliers to Production
                        </Button>
                    }
                />
            )}
        </Card>
    );
};

export default RecommendationsSection;
